import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, Eye, EyeOff, Loader2, Lock, Mail, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { publicAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';

const transformCloudinary = (url, transformation) => {
  if (!url?.includes('/image/upload/')) return url;
  return url.replace('/image/upload/', `/image/upload/${transformation}/`);
};

export const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [storefront, setStorefront] = useState(null);

  useEffect(() => {
    document.title = 'Sign In · URBNSW ERP';
    publicAPI.getStorefrontStatus()
      .then((response) => setStorefront(response.data))
      .catch(() => setStorefront(null));
  }, []);

  const branding = storefront?.branding || {};
  const brandName = branding.name || 'URBNSW';
  const brandLogo = branding.header_logo || branding.logo;
  const heroAsset = storefront?.media?.hero_background
    || storefront?.media?.campaign_primary
    || storefront?.media?.page_primary;

  const heroUrl = useMemo(
    () => transformCloudinary(heroAsset?.url, 'f_auto,q_auto:eco,c_fill,g_auto,w_1600,h_1800'),
    [heroAsset?.url],
  );
  const logoUrl = useMemo(
    () => transformCloudinary(brandLogo?.url, 'f_auto,q_auto/e_trim/c_limit,w_620,h_180'),
    [brandLogo?.url],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="relative min-h-[100dvh] overflow-hidden bg-[#080808] text-white"
      data-testid="login-page"
      style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.22] [background-image:radial-gradient(rgba(255,255,255,0.18)_1px,transparent_1px)] [background-size:28px_28px]" />
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#39FF14]/[0.035] blur-3xl" />

      <div className="relative z-10 grid min-h-[100dvh] lg:grid-cols-[1.08fr_0.92fr]">
        <section className="relative hidden min-h-[100dvh] overflow-hidden border-r border-white/10 lg:block">
          {heroUrl ? (
            <img src={heroUrl} alt={heroAsset?.alt || 'URBNSW campaign'} className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-[linear-gradient(145deg,#171717_0%,#080808_48%,#111_100%)]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-black/55" />

          <div className="relative flex h-full min-h-[100dvh] flex-col justify-between p-10 xl:p-14">
            <BrandIdentity logoUrl={logoUrl} brandName={brandName} />

            <div className="max-w-xl pb-6">
              <div className="mb-6 flex items-center gap-3">
                <span className="h-px w-12 bg-[#39FF14]" />
                <span className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#39FF14]">
                  Operations / Finance / Storefront
                </span>
              </div>
              <h1 className="text-6xl font-black uppercase leading-[0.87] tracking-[-0.065em] xl:text-8xl">
                Run the label.<br />
                <span className="text-white/45">Own the flow.</span>
              </h1>
              <p className="mt-7 max-w-lg text-base leading-7 text-white/65 xl:text-lg">
                One command centre for catalogue, inventory, orders, manufacturing, finance, and people.
              </p>
            </div>

            <div className="flex items-center justify-between border-t border-white/15 pt-5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/40">
              <span>URBNSW ERP 2.0</span>
              <span>Secure staff access</span>
            </div>
          </div>
        </section>

        <section className="flex min-h-[100dvh] items-center justify-center px-5 py-10 sm:px-10 lg:px-12 xl:px-20">
          <div className="w-full max-w-md">
            <div className="mb-12 lg:hidden">
              <BrandIdentity logoUrl={logoUrl} brandName={brandName} />
            </div>

            <div className="mb-10">
              <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#39FF14]">Staff portal</p>
              <h2 className="mt-4 text-4xl font-black tracking-[-0.045em] sm:text-5xl">Welcome back.</h2>
              <p className="mt-3 text-sm leading-6 text-white/50">
                Sign in with your approved ERP account to continue.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              {error && (
                <Alert variant="destructive" className="border-red-900/80 bg-red-950/45 text-red-100" data-testid="login-error">
                  <AlertCircle className="h-4 w-4 text-red-400" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <label htmlFor="email" className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/55">
                  Email address
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@urbnsw.com"
                    required
                    autoComplete="email"
                    data-testid="email-input"
                    className="h-14 rounded-none border-white/15 bg-white/[0.045] pl-11 text-white placeholder:text-white/25 focus:border-[#39FF14] focus-visible:ring-1 focus-visible:ring-[#39FF14]/55 focus-visible:ring-offset-0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/55">
                  Password
                </label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    data-testid="password-input"
                    className="h-14 rounded-none border-white/15 bg-white/[0.045] px-11 text-white placeholder:text-white/25 focus:border-[#39FF14] focus-visible:ring-1 focus-visible:ring-[#39FF14]/55 focus-visible:ring-offset-0"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-2 text-white/35 transition-colors hover:text-white"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs text-white/35">
                  <ShieldCheck className="h-4 w-4 text-[#39FF14]/75" />
                  Encrypted access
                </div>
                <Link to="/forgot-password" className="text-xs font-medium text-white/50 transition-colors hover:text-[#39FF14]">
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-btn"
                className="group h-14 w-full rounded-none bg-[#39FF14] text-sm font-black uppercase tracking-[0.2em] text-black shadow-none transition-colors hover:bg-[#2fe012] disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <span className="flex items-center justify-center gap-3">
                    Enter ERP
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-8 border-t border-white/10 pt-6">
              <p className="text-sm text-white/35">
                Need access?{' '}
                <Link to="/register" className="font-semibold text-white transition-colors hover:text-[#39FF14]" data-testid="register-link">
                  Request an ERP account
                </Link>
              </p>
            </div>

            <p className="mt-12 text-[9px] font-semibold uppercase tracking-[0.2em] text-white/20">
              © {new Date().getFullYear()} Urban Streetwears (Pvt) Ltd
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

function BrandIdentity({ logoUrl, brandName }) {
  return (
    <div className="flex items-center gap-3">
      {logoUrl ? (
        <img src={logoUrl} alt={`${brandName} logo`} className="max-h-12 max-w-[230px] object-contain object-left" />
      ) : (
        <>
          <div className="flex h-10 w-10 items-center justify-center border border-[#39FF14]/50 bg-black text-sm font-black text-[#39FF14]">
            U
          </div>
          <span className="text-sm font-black uppercase tracking-[0.22em]">{brandName}</span>
        </>
      )}
    </div>
  );
}

export default Login;
